import { useRouter } from 'expo-router'
import type React from 'react'
import { StyleSheet, View } from 'react-native'
import { ActivityIndicator, Text } from 'react-native-paper'
import { Button } from '@/ui/Button'

export const InvalidInviteState: React.FC = () => {
    const router = useRouter()

    return (
        <View style={styles.container}>
            <View style={styles.headerContainer}>
                <Text variant="headlineLarge">Invalid Invitation</Text>
                <Text variant="bodyMedium" style={styles.subtitle}>
                    The invitation link is invalid or has expired.
                </Text>
            </View>
            <Button
                title="Go to Sign In"
                mode="contained"
                style={styles.button}
                onPress={() => router.push('/auth/sign-in')}
            />
        </View>
    )
}

export const LoadingState: React.FC<{ message: string }> = ({ message }) => (
    <View style={[styles.container, styles.loadingContainer]}>
        <ActivityIndicator size="large" />
        <Text variant="bodyMedium">{message}</Text>
    </View>
)

export const ErrorState: React.FC<{ error: string }> = ({ error }) => {
    const router = useRouter()

    return (
        <View style={styles.container}>
            <View style={styles.headerContainer}>
                <Text variant="headlineLarge">Error</Text>
                <Text variant="bodyMedium" style={styles.errorText}>
                    {error}
                </Text>
            </View>
            <Button
                title="Go to Sign In"
                mode="contained"
                style={styles.button}
                onPress={() => router.push('/auth/sign-in')}
            />
        </View>
    )
}

interface WrongRecipientStateProps {
    invitedEmail?: string | null
    onSignUpWithInvitedEmail: () => void
}

export const WrongRecipientState: React.FC<WrongRecipientStateProps> = ({
    invitedEmail,
    onSignUpWithInvitedEmail,
}) => {
    const router = useRouter()

    return (
        <View style={styles.container}>
            <View style={styles.headerContainer}>
                <Text variant="headlineLarge">Different Email Address</Text>
                <Text variant="bodyMedium" style={styles.subtitle}>
                    This invitation was sent to a different email address than the one you're
                    currently signed in with.
                </Text>
                {invitedEmail && (
                    <Text variant="bodyMedium" style={styles.invitedEmail}>
                        Invitation sent to: {invitedEmail}
                    </Text>
                )}
                <Text variant="bodyMedium" style={styles.subtitle}>
                    Would you like to sign up with the invited email address instead?
                </Text>
            </View>

            <View style={styles.buttonContainer}>
                <Button
                    title="Yes, Sign Up with Invited Email"
                    mode="contained"
                    onPress={onSignUpWithInvitedEmail}
                    style={styles.button}
                />
                <Button
                    title="Sign In with Different Account"
                    mode="outlined"
                    style={styles.button}
                    onPress={() => router.push('/auth/sign-in')}
                />
            </View>
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        padding: 16,
        gap: 16,
        maxWidth: 400,
        width: '100%',
        alignSelf: 'center',
    },
    loadingContainer: {
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerContainer: {
        gap: 8,
        marginBottom: 16,
    },
    subtitle: {
        color: '#374151',
    },
    errorText: {
        color: '#dc2626',
    },
    invitedEmail: {
        color: '#3b82f6',
        fontWeight: '600',
    },
    buttonContainer: {
        gap: 12,
    },
    button: {
        paddingVertical: 8,
    },
})
